import { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLanguage } from '@/contexts/LanguageContext';
import { t } from '@/i18n/translations';

export const StrategicCommitment = () => {
  const ref = useRef(null);
  const { lang } = useLanguage();
  const tx = t(lang).strategic;

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      // Kartica vstopi
      gsap.fromTo('.sc-card',
        { opacity: 0, y: 50, scale: 0.97 },
        { opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'power3.out',
          scrollTrigger: { trigger: ref.current, start: 'top 80%' } }
      );
      // Badge se pojavi z zamikom
      gsap.fromTo('.sc-badge',
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration: 0.7, ease: 'back.out(1.8)', delay: 0.3,
          scrollTrigger: { trigger: ref.current, start: 'top 75%' } }
      );
      // Naslov beseda po beseda (stagger)
      gsap.fromTo('.sc-headline',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out', delay: 0.5,
          scrollTrigger: { trigger: ref.current, start: 'top 75%' } }
      );
      // Besedilo
      gsap.fromTo('.sc-body',
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out', delay: 0.75,
          scrollTrigger: { trigger: ref.current, start: 'top 75%' } }
      );
      // Signature pulsira enkrat
      gsap.fromTo('.sc-signature',
        { opacity: 0, letterSpacing: '0.05em' },
        { opacity: 1, letterSpacing: '0.1em', duration: 1.2, ease: 'power2.out', delay: 1.1,
          scrollTrigger: { trigger: ref.current, start: 'top 75%' } }
      );
      // Accent liniji zrasteta
      gsap.fromTo('.sc-line',
        { scaleX: 0 },
        { scaleX: 1, duration: 1, ease: 'power2.out', delay: 1.3, transformOrigin: 'center',
          scrollTrigger: { trigger: ref.current, start: 'top 75%' } }
      );
      // Radial glow pulse
      gsap.to('.sc-glow',
        { opacity: 0.14, scale: 1.15, duration: 3, ease: 'sine.inOut', repeat: -1, yoyo: true }
      );
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section id="strategic-commitment" className="scroll-section relative min-h-screen flex items-center overflow-hidden bg-[#040d18]">
      {/* Animated radial glow */}
      <div className="sc-glow absolute inset-0 pointer-events-none" style={{ opacity: 0.07 }}>
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(ellipse at center, rgba(200,117,51,1) 0%, transparent 70%)' }}
        />
      </div>

      <div ref={ref} className="relative max-w-[820px] mx-auto px-4 sm:px-6 text-center">
        <div
          className="sc-card relative rounded-[20px] px-8 sm:px-12 py-12 sm:py-16"
          style={{
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          }}
        >
          {/* Top accent line */}
          <div className="sc-line absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#C87533]/70 to-transparent" />

          {/* Badge */}
          <div className="sc-badge inline-flex items-center gap-2 mb-8 px-4 py-1.5 rounded-full border border-[#C87533]/30 bg-[#C87533]/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C87533] animate-pulse" />
            <span className="font-mono text-xs text-[#C87533] uppercase tracking-widest">{tx.badge}</span>
          </div>

          <h2
            className="sc-headline font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8 tracking-tight"
            style={{ letterSpacing: '-0.01em' }}
          >
            {tx.headline1}<br />
            <span className="text-copper-gradient">{tx.headlineAccent}</span>
          </h2>

          <p
            className="sc-body text-white/65 text-base sm:text-lg max-w-2xl mx-auto mb-12"
            style={{ lineHeight: 1.75 }}
          >
            {tx.body}
          </p>

          <div className="flex items-center justify-center gap-4">
            <div className="h-px w-12 bg-[#C87533]/30" />
            <span
              className="sc-signature font-mono text-sm"
              style={{
                color: '#C87533',
                textShadow: '0 0 24px rgba(200,117,51,0.6)',
                letterSpacing: '0.1em',
              }}
            >
              {tx.signature}
            </span>
            <div className="h-px w-12 bg-[#C87533]/30" />
          </div>

          {/* Bottom accent line */}
          <div className="sc-line absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#C87533]/70 to-transparent" />
        </div>
      </div>
    </section>
  );
};
